import * as React from 'react'
import { createContext, PropsWithChildren, ReactElement, useEffect, useMemo, useRef, useState } from 'react'
import { AxiosError } from 'axios'
import {
    AccountInfo,
    AuthenticationResult,
    BrowserCacheLocation,
    IPublicClientApplication,
    LogLevel,
    PublicClientApplication,
} from '@azure/msal-browser'
import { useMount } from '@fluentui/react-hooks'
import { asBoolean } from '../helpers/functionHelper'
import { isOfficeInitialized } from '../helpers/outlookHelper'
import { hasRefreshToken } from '../helpers/msalCache'
import { jwtDecode, JwtPayload } from 'jwt-decode'
import { DateTime } from 'luxon'
import useAxios from '../hooks/useAxios'
import { ServerAuthorizationTokenResponse } from '@azure/msal-common/dist/response/ServerAuthorizationTokenResponse'
import { Role } from '../hooks/useRoles'
import { Destination, graphBaseUrl } from './AxiosContext'
import useLocalStorage from '../hooks/useLocalStorage'
import {
    clearLoginJourney,
    flushLoginTelemetry,
    markInitialLoginComplete,
    markTokensFetched,
    trackLoginFailure,
    trackLoginPhase,
} from '../helpers/loginTelemetry'
import { bootstrapRetryDelayMs, BootstrapTokenStatus, classifyBootstrapToken } from '../helpers/bootstrapRetry'
import {
    classifyOfficeError,
    clearSsoPenalty,
    evaluateSsoGate,
    MIN_SSO_INTERVAL_MS,
    recordSsoCall,
    recordSsoOutcome,
    SsoOutcome,
    ssoRateErrorCeilingReached,
} from '../helpers/officeSsoGate'
import { readSsoGateState, writeSsoGateState } from '../helpers/officeSsoGateStorage'

export const AUTHENTICATION_SCOPES: string[] = [`${process.env.BOOTSTRAP_API_URI}/access_as_user`, 'openid', 'email', 'profile']

// An access token is only accepted if it still has more than this much life left (see `isTokenNotExpired`).
const ACCESS_TOKEN_EXPIRY_BUFFER_MS = 60_000
// A bootstrap token is only accepted if it still has more than this much life left. It must stay strictly
// larger than ACCESS_TOKEN_EXPIRY_BUFFER_MS: an access token derived (on-behalf-of) from the bootstrap token
// inherits an EntraID expiry no later than the bootstrap's, so a bootstrap that is "valid enough to derive
// from" must leave the resulting access token comfortably above the 60s acceptance buffer even after the
// acquisition round-trip and clock skew. Otherwise we'd accept a bootstrap that can only yield an
// already-expired access token, spinning the tokens-acquisition effect forever.
const BOOTSTRAP_EXPIRY_BUFFER_MS = 90_000

export type AzureTokenPayload = OfficeTokenPayload & {
    roles: Role[]
}

export type OfficeTokenPayload = {
    oid: string // UUID of user
    name: string // User's display name
    preferred_username: string // E-mail address of user
    unique_name: string // E-mail address of user
    upn: string // unified name, most often the e-mail address
    scp: string // Space-separated scopes
    tid: string // Tenant ID
    uti: string // Unique, per-token identifier that is case-sensitive.
} & JwtPayload

export type Host = {
    RowKey: string
    Name: string
    Backend: string
    ClientId: string
    ClientPath: string
    DriveId: string
    ReplicationMail: string
    tokens: Tokens
}

export enum SecurityAuthority {
    Hammock = 'hammock', // Connect to HAMMOCK in the user's tenant.
    SharePoint = 'sharepoint', // Connect to MS Graph in the Hammock.nl tenant.
    Exchange = 'exchange', // Connect to MS Graph in the user's tenant.
    WebPubSub = 'webpubsub', // Connect to A Web Pub Sub socket in the user's hub.
}

export const DestinationAuthorityMap = new Map<Destination, SecurityAuthority>([
    [Destination.Hammock, SecurityAuthority.Hammock],
    [Destination.Exchange, SecurityAuthority.Exchange],
    [Destination.ReplicationEmail, SecurityAuthority.Exchange],
    [Destination.Email, SecurityAuthority.Exchange],
    [Destination.PrivateEmail, SecurityAuthority.Exchange],
    [Destination.SharePoint, SecurityAuthority.SharePoint],
    [Destination.Documents, SecurityAuthority.SharePoint],
])

type PartialServerAuthorizationTokenResponse = Pick<ServerAuthorizationTokenResponse, 'access_token' | 'refresh_token' | 'token_type'>

export type WebPubSubToken = PartialServerAuthorizationTokenResponse & {
    hub: string
    url: string
    issued_at: string
    expires_at: string
}

export type Tokens = {
    [SecurityAuthority.Hammock]?: ServerAuthorizationTokenResponse
    [SecurityAuthority.SharePoint]?: ServerAuthorizationTokenResponse
    [SecurityAuthority.Exchange]?: ServerAuthorizationTokenResponse
    [SecurityAuthority.WebPubSub]?: WebPubSubToken
}

export type AuthorizationContextType = {
    identified: boolean
    authenticated: boolean
    user?: AccountInfo
    mailbox?: string
    bootstrapToken?: string
    hosts?: Host[]
    /**
     * The router could not tell us which environments this user may enter (a terminal 4xx on
     * /api/hosts). Distinct from `hosts === []`, which is the router positively answering "none".
     */
    hostsUnavailable: boolean
    host?: Host
    setHost: (host: Host) => void
    tokens?: Tokens
    roles?: Role[]
    /**
     * Stable identifier for the (user, environment) tuple. Defined only when tokens for the currently
     * selected host have actually loaded. `undefined` while a host switch is in flight, while tokens
     * are expired, before initial token load, and after logout. Use this as a `useEffect` dep to
     * reload per-environment caches (e.g. `/global`) — string identity is stable across token refreshes.
     */
    environmentKey?: string
    /**
     * Bumps every time `tokens` is replaced with a fresh server response. Use this as a `useEffect`
     * dep if you specifically need to react to new tokens (re-decoding claims, refreshing a socket
     * that embeds the token). Most consumers should depend on `environmentKey` instead.
     */
    tokensRevision: number
    updateTokens: (authority: SecurityAuthority) => Promise<ServerAuthorizationTokenResponse | WebPubSubToken>
    getBaseUrl: (destination: Destination) => string | undefined
    reset: () => void
    /**
     * Office keeps throttling our token requests (error 13013) and we have stopped asking by
     * ourselves. Set only in the add-in, only after `SSO_RATE_ERROR_CEILING` consecutive rate
     * errors — a dead end the user needs told, not another spinner. `retryAt` is when the current
     * penalty lifts; `retryBootstrap` asks again straight away.
     */
    bootstrapBlocked?: { retryAt: DateTime }
    retryBootstrap: () => void
}

const AuthorizationContext = createContext<AuthorizationContextType>({
    identified: false,
    authenticated: false,
    user: undefined,
    mailbox: undefined,
    bootstrapToken: undefined,
    hostsUnavailable: false,
    host: undefined,
    setHost: () => {},
    tokens: undefined,
    roles: undefined,
    environmentKey: undefined,
    tokensRevision: 0,
    updateTokens: () => Promise.resolve<ServerAuthorizationTokenResponse | WebPubSubToken>(undefined),
    getBaseUrl: () => graphBaseUrl,
    reset: () => {
        clearLocalStorageGlobals()
        window.location.reload()
    },
    bootstrapBlocked: undefined,
    retryBootstrap: () => {},
})

type AuthenticationStorageDto = Pick<AuthorizationContextType, 'identified' | 'authenticated' | 'user' | 'mailbox'>
type AuthorizationHostStorageDto = Pick<AuthorizationContextType, 'bootstrapToken' | 'hosts' | 'host'>
type AuthorizationTokensStorageDto = Pick<AuthorizationContextType, 'tokens' | 'roles'>

function checkSharedMailboxEmail(setMailbox: (mailbox: string) => void) {
    if (isOfficeInitialized() && Office.context.mailbox.item?.getSharedPropertiesAsync) {
        Office.context.mailbox.item?.getSharedPropertiesAsync?.(result => {
            if (result.status === Office.AsyncResultStatus.Failed) {
                console.debug(
                    'AuthorizationProvider:',
                    'checkSharedMailboxEmail: Error while detecting shared mailbox:',
                    result.error.code,
                    result.error.name,
                    result.error.message,
                )
                setMailbox(Office.context.mailbox.userProfile.emailAddress)
                return
            }
            console.debug('AuthorizationProvider:', 'checkSharedMailboxEmail: Shared mailbox detected: ', JSON.stringify(result.value))
            setMailbox(result.value.targetMailbox)
        })
    } else if (isOfficeInitialized()) {
        console.debug(
            'AuthorizationProvider:',
            "checkSharedMailboxEmail: No message selected or the message isn't part of a shared mailbox:",
            Office.context.mailbox.userProfile.emailAddress,
        )
        setMailbox(Office.context.mailbox.userProfile.emailAddress)
    }
}

const isBootstrapTokenFresh = (token: string | undefined): token is string => classifyBootstrapToken(token, BOOTSTRAP_EXPIRY_BUFFER_MS) === 'fresh'

const parseBootstrapToken = (
    token: string,
    user: AccountInfo,
    setUser: (token: AccountInfo) => void,
    bootstrapTokenRef: React.MutableRefObject<string | undefined>,
    setBootstrapTokenExpiresAt: (expires: DateTime) => void,
): BootstrapTokenStatus => {
    const status = classifyBootstrapToken(token, BOOTSTRAP_EXPIRY_BUFFER_MS)

    let decodedToken = undefined
    try {
        decodedToken = token && jwtDecode<OfficeTokenPayload>(token)
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (_e) {
        // classifyBootstrapToken already logged the decode failure
    }
    console.debug(
        DateTime.now().toUTC().toISO(),
        `AuthorizationProvider: parseBootstrapToken: ${status} token:`,
        decodedToken ? decodedToken.uti : undefined,
        '. Expires at ',
        decodedToken && decodedToken.exp ? DateTime.fromSeconds(decodedToken.exp, { zone: 'UTC' }).toISO() : undefined,
    )

    if (status !== 'fresh') {
        // 'invalid': Office.auth.getAccessToken can resolve with an empty string; storing it would
        // make acquireHosts/acquireTokens send `Authorization: Bearer ` to the router (HAM-1001,
        // "expected 3 parts, but got 0").
        // 'stale': Office SSO serves the same cached token until it is almost expired, so a
        // near-/just-expired token is expected around expiry and must not drive the refresh timer
        // from its own `exp` (that floors the timer to 3s and hammers getAccessToken into
        // throttling error 13013). Either way: keep the ref unchanged, adopt no expiry, and leave
        // scheduling the next attempt to the caller.
        return status
    }

    bootstrapTokenRef.current = token
    setBootstrapTokenExpiresAt(DateTime.fromSeconds(decodedToken?.exp, { zone: 'UTC' }))

    if (isOfficeInitialized() && decodedToken && user?.localAccountId !== decodedToken.oid) {
        setUser({
            homeAccountId: `${decodedToken.oid}.${decodedToken.tid}`,
            environment: 'login.windows.net',
            tenantId: decodedToken.tid,
            username: decodedToken.preferred_username?.toLowerCase() || decodedToken.unique_name?.toLowerCase() || decodedToken.upn?.toLowerCase(),
            localAccountId: decodedToken.oid,
            name: decodedToken.name,
        })
    }

    return 'fresh'
}

const getTokenExpiryDateTime = (response?: PartialServerAuthorizationTokenResponse): DateTime => {
    const token = response && jwtDecode<OfficeTokenPayload>(response?.access_token)
    if (token?.exp) {
        return DateTime.fromSeconds(token?.exp, { zone: 'UTC' })
    } else if (response) {
        console.warn('no response found', token?.uti)
    } else if (token) {
        console.warn('no expiry found in token', token?.uti)
    }
    return DateTime.now().toUTC()
}

const isTokensNotExpired = (response?: Tokens) => {
    return (
        isTokenNotExpired(response?.[SecurityAuthority.Hammock]) &&
        isTokenNotExpired(response?.[SecurityAuthority.Exchange]) &&
        isTokenNotExpired(response?.[SecurityAuthority.SharePoint]) &&
        isTokenNotExpired(response?.[SecurityAuthority.WebPubSub])
    )
}

export const isTokenNotExpired = (response?: PartialServerAuthorizationTokenResponse) => {
    if (!response) {
        return false
    }
    const exp = getTokenExpiryDateTime(response)
    const now = DateTime.now().toUTC()
    return exp.toMillis() - now.toMillis() - ACCESS_TOKEN_EXPIRY_BUFFER_MS > 0
}

const isTransientNetworkError = (error: unknown): boolean => {
    const axiosError = error as AxiosError & { cause?: { code?: string; message?: string } }
    const combinedErrorText = [axiosError?.code, axiosError?.name, axiosError?.message, axiosError?.cause?.code, axiosError?.cause?.message]
        .filter(Boolean)
        .join(' ')
        .toUpperCase()

    return combinedErrorText.includes('ERR_NETWORK') || combinedErrorText.includes('NETWORK ERROR')
}

const clearLocalStorageKeys = (description: string, matches: (key: string) => boolean) => {
    try {
        console.debug(DateTime.now().toUTC().toISO(), `AuthorizationProvider: clearing ${description} localStorage keys`)
        for (let i = window.localStorage.length - 1; i >= 0; i--) {
            const key = window.localStorage.key(i)
            if (key && matches(key)) {
                window.localStorage.removeItem(key)
            }
        }
    } catch (e) {
        console.error(DateTime.now().toUTC().toISO(), `AuthorizationProvider: clearing ${description} localStorage keys failed`, e)
    }
}

const clearLocalStorageGlobals = () => clearLocalStorageKeys('global-auth', key => key.toLowerCase().startsWith('global-auth'))

/**
 * <p>This context is responsible for detecting the current user, the current mailbox and fetching a bootstrap token.</p>
 * <p>There are 2 situations:
 * - Standalone: The current mailbox is always the user's mailbox. No mailbox switch detection is performed.
 * - Outlook: The current mailbox is either the user's mailbox or a shared mailbox (info@ or support@).
 *   - Outlook will inform us of the current mailbox's e-mail address.
 *   - Mailbox switch detection is added, so Outlook will inform us of the current mailbox's e-mail address when switching between e-mail messages.
 *
 * </p>
 */
export function AuthorizationProvider(props: PropsWithChildren): ReactElement {
    const axios = useAxios()

    /**
     * The client responsible for communicating with MS to gain a bootstrapToken.
     */
    const [pca, setPca] = useState<IPublicClientApplication>()
    const [isPCAReady, setIsPCAReady] = useState<boolean>(false)

    /**
     * A list of variables required to log in / refresh tokens.
     */
    const [user, setUser] = useState<AccountInfo>()
    const [mailbox, setMailbox] = useState<string>()

    const bootstrapTokenRef = useRef<string>()
    const bootstrapTokenRequestRef = useRef<Promise<void>>()
    const [bootstrapTokenExpiresAt, setBootstrapTokenExpiresAt] = useState<DateTime>()
    const bootstrapTokenTimeoutRef = useRef<ReturnType<typeof setTimeout>>()
    /**
     * Consecutive bootstrap acquisitions that yielded no fresh token (stale/invalid/error);
     * reset to 0 the moment a fresh token lands. Drives the exponential retry backoff.
     */
    const bootstrapRetryAttemptRef = useRef(0)
    /**
     * When this page load started. Only used to tell a gate block caused by *this* page load apart
     * from one inherited from a previous one — the difference between "we are asking too fast" and
     * "the task pane reloaded into a throttle it did not cause", which is 730 of 898 cases.
     */
    const pageLoadStartedAtRef = useRef(Date.now())
    const [bootstrapBlocked, setBootstrapBlocked] = useState<{ retryAt: DateTime }>()

    const [hosts, setHosts] = useState<Host[]>()
    const [host, setHost] = useState<Host>()
    const hostsRequestRef = useRef<Promise<void>>()
    /**
     * The router answered /api/hosts with a client error we cannot recover from, so we do not know
     * which environments this user may enter. Retrying only repeats the same answer, so this stops
     * the hosts effect and `updateTokens` from asking again and lets `SelectHost` explain instead of
     * showing an endless spinner. Mirrored in a ref because `updateTokens` reads it from a promise
     * chain that may hold an older render's closure.
     */
    const [hostsUnavailable, setHostsUnavailable] = useState(false)
    const hostsUnavailableRef = useRef(false)
    const markHostsUnavailable = (unavailable: boolean) => {
        hostsUnavailableRef.current = unavailable
        setHostsUnavailable(unavailable)
    }

    const [tokens, setTokens] = useState<Tokens>()
    const tokensRequestRef = useRef<Promise<void | Tokens>>()
    const [tokensExpiresAt, setTokensExpiresAt] = useState<DateTime>()
    const tokensTimeoutRef = useRef<ReturnType<typeof setTimeout>>()
    /**
     * Identifies the host that the currently held `tokens` were fetched for. Used together with
     * `host.RowKey` to detect that we are between a host switch and the arrival of new tokens.
     */
    const [tokensHostKey, setTokensHostKey] = useState<string>()
    /**
     * Monotonically increasing counter that bumps every time `tokens` is replaced with a fresh
     * server response. Exposed via the context as `tokensRevision`.
     */
    const [tokensRevision, setTokensRevision] = useState<number>(0)
    /**
     * Guards the one-time `Login.Authenticated` telemetry event for this page load.
     */
    const initialLoginTrackedRef = useRef(false)

    const bootstrapTokenValid = useMemo(() => {
        if (!bootstrapTokenRef.current) {
            return false
        }
        let decodedToken = undefined
        try {
            decodedToken = jwtDecode<OfficeTokenPayload>(bootstrapTokenRef.current)
        } catch (e) {
            console.error(e)
        }
        if (decodedToken && decodedToken.exp) {
            const exp = DateTime.fromSeconds(decodedToken.exp, { zone: 'UTC' })
            const now = DateTime.now().toUTC()
            return exp.toMillis() - now.toMillis() > BOOTSTRAP_EXPIRY_BUFFER_MS
        }

        return false
    }, [bootstrapTokenExpiresAt])

    const identified = useMemo(() => !!user && bootstrapTokenValid, [user, bootstrapTokenValid])

    const authenticated = useMemo(() => !!host && !!tokens && isTokensNotExpired(tokens), [host, tokens])

    const roles = useMemo<Role[]>(
        () =>
            tokens?.[SecurityAuthority.Hammock] ? jwtDecode<AzureTokenPayload>(tokens?.[SecurityAuthority.Hammock].access_token)?.roles || [] : [],
        [tokens],
    )

    /**
     * Stable identifier for the (user, environment) tuple. See `AuthorizationContextType.environmentKey`
     * for the contract. Defined only when tokens for the currently selected host are present and not
     * expired, so consumers can `useEffect(() => reload(), [environmentKey])` without racing token load.
     */
    const environmentKey = useMemo<string | undefined>(() => {
        if (!host || !user || !tokens) {
            return undefined
        }
        if (tokensHostKey !== host.RowKey) {
            return undefined
        }
        if (!isTokensNotExpired(tokens)) {
            return undefined
        }
        return `${host.RowKey}:${user.localAccountId}`
    }, [host, user, tokens, tokensHostKey])

    const { loaded: loadedFirstVisit, values: firstVisit } = useLocalStorage<boolean, boolean>({
        key: 'firstVisit',
        defaultValue: true,
        fromJson: values => values,
        toJson: values => values,
    })

    /**
     * In Outlook, scope `AuthenticationStorage` (and the derived `storagePrefix`) by the currently
     * signed-in Outlook account so switching between accounts in different tenants on Outlook for
     * Mac picks up the right cached identity instead of replaying the previous account's. The add-in
     * iframe reloads on account switch, so each mount sees the current account. `undefined` outside
     * Outlook leaves the storage under the default `'global'` key, preserving standalone-mode behavior.
     *
     * `userProfile.emailAddress` can lag behind `Office.onReady` on a cold cross-account reload: if we
     * captured it once at mount it would be `undefined`, silently routing add-in storage to the
     * standalone `'global'` bucket and preventing a warm restore. So we seed it if already available
     * and otherwise poll briefly until it appears; the `load` gate below blocks any `'global'`
     * read/write until it does.
     */
    const [outlookAccountKey, setOutlookAccountKey] = useState<string | undefined>(() =>
        isOfficeInitialized() ? Office.context.mailbox?.userProfile?.emailAddress?.toLowerCase() : undefined,
    )
    useEffect(() => {
        if (!isOfficeInitialized() || outlookAccountKey) {
            return undefined
        }
        let attempts = 0
        const interval = setInterval(() => {
            const email = Office.context.mailbox?.userProfile?.emailAddress?.toLowerCase()
            if (email || ++attempts >= 25) {
                clearInterval(interval)
                if (email) {
                    setOutlookAccountKey(email)
                }
            }
        }, 200)
        return () => clearInterval(interval)
    }, [outlookAccountKey])

    const {
        values: authenticationStorage,
        setValues: setAuthenticationStorage,
        loaded: authenticationStorageLoaded,
    } = useLocalStorage<AuthenticationStorageDto, AuthenticationStorageDto>({
        hostKey: outlookAccountKey,
        key: 'AuthenticationStorage',
        fromJson: values => values,
        toJson: values => values,
        defaultValue: undefined,
        // In the add-in, wait for the account email before touching localStorage so identity never
        // lands in the standalone `'global'` bucket. Standalone (no Office) loads immediately.
        load: !isOfficeInitialized() || outlookAccountKey !== undefined,
        logging: true,
    })
    const [isUserHydrated, setIsUserHydrated] = useState<boolean>(false)

    /**
     * Stays `undefined` until we know which user owns the host storage:
     * - Outlook: `outlookAccountKey` is known from mount, so the prefix is `pathname-email`
     *   immediately and `AuthorizationHostStorage` hydrates without a key-change re-read.
     * - Standalone: wait for `homeAccountId` to hydrate from `AuthenticationStorage` (which itself
     *   needs `authenticationStorageLoaded`). Returning `undefined` here keeps `useLocalStorage`'s
     *   `load` gate closed and prevents orphan writes to `global-AuthorizationHostStorage` during
     *   the pre-login window (first visit, post-reset, or after a sign-out).
     */
    const storagePrefix = useMemo<string>(() => {
        const userPart = outlookAccountKey ?? (authenticationStorageLoaded ? authenticationStorage?.user?.homeAccountId : undefined)
        if (!userPart) {
            return undefined
        }
        return [window.location.pathname.replaceAll('/', ''), userPart].filter(Boolean).join('-')
    }, [authenticationStorageLoaded, authenticationStorage, outlookAccountKey])
    /**
     * During an account switch there is a window where `user` is already the new account (set by
     * `handleRedirectPromise`) while `authenticationStorage` — and therefore `storagePrefix` and the
     * loaded `authorizationHostStorage` values — still belong to the previous account. Hydrating or
     * persisting host storage in that window mixes the two accounts: the previous account's `host`
     * gets adopted (and persisted) for the new account, whose tokens request then 404s on a host it
     * has no access to (see logs/AuthorizationContext/190-standalone-random-login.log in the claude
     * repo). In Outlook mode `storagePrefix` is derived from `outlookAccountKey`, so it cannot lag.
     */
    const storageMatchesUser = outlookAccountKey !== undefined || authenticationStorage?.user?.homeAccountId === user?.homeAccountId
    const {
        values: authorizationHostStorage,
        setValues: setAuthorizationHostStorage,
        loaded: authorizationHostStorageLoaded,
    } = useLocalStorage<AuthorizationHostStorageDto, AuthorizationHostStorageDto>({
        hostKey: storagePrefix,
        key: 'AuthorizationHostStorage',
        fromJson: values => values,
        toJson: values => values,
        defaultValue: undefined,
        load: storagePrefix !== undefined,
        logging: true,
    })
    const [isBootstrapHydrated, setIsBootstrapHydrated] = useState<boolean>(false)

    const tokensStoragePrefix = useMemo<string>(
        () => (!host ? undefined : [host?.RowKey, storagePrefix].filter(Boolean).join('-')),
        [host, storagePrefix],
    )
    const {
        values: authorizationTokensStorage,
        setValues: setAuthorizationTokensStorage,
        loaded: authorizationTokensStorageLoaded,
    } = useLocalStorage<AuthorizationTokensStorageDto, AuthorizationTokensStorageDto>({
        hostKey: tokensStoragePrefix,
        key: 'AuthorizationTokensStorage',
        fromJson: values => values,
        toJson: values => values,
        defaultValue: undefined,
        load: authorizationHostStorageLoaded && tokensStoragePrefix !== undefined,
        logging: true,
    })
    const [isHydrated, setIsHydrated] = useState<boolean>(false)

    useMount(() => trackLoginPhase('started'))

    /**
     * Initialize the OAuth client. This Client will handle any login redirect when in standalone mode.
     * When in Outlook, this client is not used.
     */
    useMount(() => {
        PublicClientApplication.createPublicClientApplication({
            auth: {
                clientId: process.env.BOOTSTRAP_CLIENT_ID,
                authority: 'https://login.microsoftonline.com/common',
                navigateToLoginRequestUrl: false,
                onRedirectNavigate: url => {
                    console.debug(DateTime.now().toUTC().toISO(), 'onRedirectNavigate', url)
                    return true
                },
            },
            cache: {
                cacheLocation: BrowserCacheLocation.LocalStorage,
            },
            system: {
                tokenRenewalOffsetSeconds: 300,
                // MSAL waits 10s by default for the hidden iframe to come back with a hash. When
                // that iframe cannot succeed at all — no AAD session in its context, or a blocked
                // third-party cookie — those are ten seconds of nothing before the redirect
                // fallback kicks in. 6s (MSAL's own pre-v3 default) still clears the successful
                // renewals, whose p90 sits under 3s. See HAM-4319.
                iframeHashTimeout: 6_000,
                loggerOptions: {
                    loggerCallback: (_level: LogLevel, message: string) => {
                        console.debug(message)
                    },
                    logLevel: Number(process.env.MSAL_LOG_LEVEL),
                    piiLoggingEnabled: asBoolean(process.env.VERBOSE_ERRORS),
                },
            },
        }).then(client => {
            console.debug(DateTime.now().toUTC().toISO(), `AuthorizationProvider: pca init complete`)
            setPca(client)

            client
                .handleRedirectPromise()
                .then((result: AuthenticationResult) => {
                    console.debug(DateTime.now().toUTC().toISO(), 'AuthorizationProvider: handleRedirectPromise', JSON.stringify(result))
                    if (result) {
                        console.debug(DateTime.now().toUTC().toISO(), 'AuthorizationProvider: handleRedirectPromise setting account')
                        setUser(result.account)
                        parseBootstrapToken(result.accessToken, user, setUser, bootstrapTokenRef, setBootstrapTokenExpiresAt)
                        trackLoginPhase('bootstrap', { mode: 'msal-redirect' })
                        setIsUserHydrated(true)
                        setIsBootstrapHydrated(true)
                    } else {
                        const activeAccount = client.getActiveAccount()
                        const currentAccounts = client.getAllAccounts()
                        console.debug(
                            DateTime.now().toUTC().toISO(),
                            'AuthorizationProvider:',
                            `handleRedirectPromise activeAccount=${activeAccount?.username}, numberOfAccounts=${currentAccounts?.length}`,
                        )
                        if (activeAccount) {
                            setUser(activeAccount)
                            setIsUserHydrated(true)
                        } else if (currentAccounts && currentAccounts.length === 1) {
                            setUser(currentAccounts[0])
                            setIsUserHydrated(true)
                        }
                    }
                    setIsPCAReady(true)
                })
                .catch(e => {
                    console.debug(DateTime.now().toUTC().toISO(), 'AuthorizationProvider: handleRedirectPromise', e)
                    trackLoginFailure('redirect-callback', e, { mode: 'msal-redirect' })
                })
        })
    })

    /**
     * Add an Async Handler for Outlook which detects if the user switches to a shared mailbox.
     */
    useMount(() => {
        if (isOfficeInitialized()) {
            console.debug(DateTime.now().toUTC().toISO(), 'AuthorizationProvider:', Office.context.mailbox.userProfile)

            if (Office.context.mailbox.addHandlerAsync) {
                Office.context.mailbox.addHandlerAsync(
                    Office.EventType.ItemChanged,
                    () => {
                        console.debug(
                            DateTime.now().toUTC().toISO(),
                            'AuthorizationProvider:',
                            'Detected switch to mailbox item:',
                            Office.context.mailbox.item?.subject,
                        )
                        checkSharedMailboxEmail(setMailbox)
                    },
                    result => {
                        if (result.status === Office.AsyncResultStatus.Failed) {
                            console.debug(
                                DateTime.now().toUTC().toISO(),
                                'AuthorizationProvider:',
                                'Error in ItemChanged handler:',
                                result.error.code,
                                result.error.name,
                                result.error.message,
                            )
                        }
                    },
                )

                // Intentionally NO cleanup here. Office.context.mailbox.removeHandlerAsync cannot
                // target a single handler — it strips EVERY ItemChanged handler on the mailbox,
                // including MessageProvider's switch detection. Removing it here (on a provider
                // remount) left the add-in stuck on the first selected item. This provider lives
                // for the app's lifetime, so we register once and never remove.
            } else {
                console.error('base.error.outdated_outlook')
            }
        }

        return undefined
    })

    /**
     * Re-arm the bootstrap acquisition in `delayMs`, flowing through the regular bootstrapToken
     * timeout (the expiry state doubles as a "retry at" time, see the timer effect below).
     *
     * Every path that declines to acquire a token right now must end here. A decline without a
     * re-arm is an indefinite login hang, which is far worse than the delay it was avoiding.
     */
    const scheduleBootstrapRetryIn = (delayMs: number, reason: string): void => {
        console.debug(DateTime.now().toUTC().toISO(), `AuthorizationProvider: scheduling bootstrap retry (${reason}) in`, delayMs, 'ms')
        setBootstrapTokenExpiresAt(DateTime.now().toUTC().plus({ milliseconds: delayMs }))
    }

    /**
     * Record how an Office SSO call ended, so the next one (in this page load or the next) knows
     * whether it is allowed. Re-reads the stored state rather than reusing the value read before
     * the call: a second task pane may have written to it while this call was in flight.
     */
    const recordSsoGateOutcome = (outcome: SsoOutcome) => {
        const now = Date.now()
        const updated = recordSsoOutcome(readSsoGateState(window.localStorage, now), outcome, now)
        writeSsoGateState(window.localStorage, updated)
        if (outcome === 'ok') {
            setBootstrapBlocked(undefined)
        }
        return updated
    }

    /**
     * The user asked us to try again. Clears the penalty — a deliberate click is the one signal
     * Office' throttle is designed to admit — but never the minimum interval, so a double click
     * still costs one call.
     */
    const retryBootstrap = () => {
        const now = Date.now()
        writeSsoGateState(window.localStorage, clearSsoPenalty(readSsoGateState(window.localStorage, now), now))
        bootstrapRetryAttemptRef.current = 0
        setBootstrapBlocked(undefined)
        scheduleBootstrapRetryIn(MIN_SSO_INTERVAL_MS, 'user asked to retry')
    }

    /**
     * Schedule the next bootstrap acquisition attempt with exponential backoff.
     *
     * There used to be a `jumpToCap` flag that raised `attempt` itself to
     * `BOOTSTRAP_RETRY_CAP_ATTEMPT` on an Office throttle, conflating the delay with the attempt
     * count — which is why a first 13013 and a fourth were indistinguishable in the telemetry.
     * Throttles are now scheduled by the SSO gate's own penalty, and `attempt` counts consecutive
     * failures and nothing else.
     */
    const scheduleBootstrapRetry = (reason: string): { attempt: number; delayMs: number } => {
        const attempt = bootstrapRetryAttemptRef.current + 1
        bootstrapRetryAttemptRef.current = attempt
        const delayMs = bootstrapRetryDelayMs(attempt)
        scheduleBootstrapRetryIn(delayMs, `${reason}, attempt ${attempt}`)
        return { attempt, delayMs }
    }

    /**
     * Acquire a bootstrap token. The request is stored in a State to prevent multiple concurrent requests.
     *
     * This function has 2 flows:
     * - Outlook: Only silently request an access token.
     * - Standalone: Attempt to silently acquire a bootstrap token and fall back to a redirect if this fails.
     */
    const acquireBootstrapTokenSilent = (): Promise<void> => {
        if (bootstrapTokenRequestRef.current) {
            return bootstrapTokenRequestRef.current
        }

        let request: Promise<void>
        if (isOfficeInitialized()) {
            // Only the Office branch is gated. MSAL is not rate-limited, and standalone login is a
            // separate risk surface that this problem does not touch.
            const now = Date.now()
            const gateState = readSsoGateState(window.localStorage, now)
            const block = evaluateSsoGate(gateState, now, pageLoadStartedAtRef.current)
            if (block) {
                console.debug(
                    DateTime.now().toUTC().toISO(),
                    `AuthorizationProvider: getBootstrapToken (office) suppressed by the SSO gate (${block.reason}), retrying in`,
                    block.waitMs,
                    'ms',
                )
                // The throttle that stops us here was usually earned in an *earlier* page load —
                // that is the whole reason the gate persists. The streak survives the reload but a
                // live `catch` does not, so the message has to be derived from the stored state as
                // well; otherwise exactly the case this was built for (a task pane reloading into
                // an inherited penalty) is the one case that still shows an endless spinner.
                if (block.reason === 'penalty' && ssoRateErrorCeilingReached(gateState)) {
                    setBootstrapBlocked({ retryAt: DateTime.now().toUTC().plus({ milliseconds: block.waitMs }) })
                }
                scheduleBootstrapRetryIn(block.waitMs, `sso-gate ${block.reason}`)
                trackLoginFailure(
                    'bootstrap',
                    { message: `Office SSO suppressed by the gate (${block.reason})`, code: `sso-gate-${block.reason}` },
                    {
                        mode: 'office-sso',
                        suppressed: true,
                        waitMs: block.waitMs,
                        crossPageLoad: block.crossPageLoad,
                        rateErrorStreak: gateState?.rateErrorStreak ?? 0,
                    },
                )
                return Promise.resolve()
            }

            const msSinceLastCall = gateState?.lastCallAt === undefined ? undefined : now - gateState.lastCallAt
            const firstCallOfPageLoad = gateState?.lastCallAt === undefined || gateState.lastCallAt < pageLoadStartedAtRef.current
            // Written *before* the call: a task pane that reloads mid-flight must still see that a
            // call was spent, otherwise the reload immediately spends another one.
            writeSsoGateState(window.localStorage, recordSsoCall(gateState, now))

            console.debug(DateTime.now().toUTC().toISO(), 'AuthorizationProvider: getBootstrapToken (office) started')
            request = Office.auth
                .getAccessToken({ allowConsentPrompt: true, allowSignInPrompt: true })
                .then(result => {
                    recordSsoGateOutcome('ok')
                    const status = parseBootstrapToken(result, user, setUser, bootstrapTokenRef, setBootstrapTokenExpiresAt)
                    if (status === 'fresh') {
                        bootstrapRetryAttemptRef.current = 0
                        trackLoginPhase('bootstrap', { mode: 'office-sso' })
                        return
                    }
                    // Office SSO serves the same cached token until it is almost expired; a stale
                    // return around expiry is expected, not an error — back off and let Outlook's
                    // cache roll over instead of provoking throttling error 13013.
                    const { attempt, delayMs } = scheduleBootstrapRetry(`office token ${status}`)
                    trackLoginFailure(
                        'bootstrap',
                        { message: `Office returned ${status} bootstrap token`, code: `${status}-bootstrap-token` },
                        { mode: 'office-sso', staleToken: status === 'stale', attempt, retryInMs: delayMs },
                    )
                })
                .catch(error => {
                    console.debug(DateTime.now().toUTC().toISO(), `AuthorizationProvider: getBootstrapToken due to error: ${JSON.stringify(error)}`)
                    const outcome = classifyOfficeError(error)
                    const gateAfter = recordSsoGateOutcome(outcome)
                    const throttled = outcome === 'rate-limited'
                    const penaltyMs = gateAfter.blockedUntil ? gateAfter.blockedUntil - Date.now() : 0

                    // Rate limited over and over: say so, instead of leaving the user on a spinner
                    // that resolves in ten minutes if at all. The retry below stays armed either
                    // way, so an unattended task pane still recovers by itself — the button is for
                    // the user who would rather not wait, and a deliberate click is the one signal
                    // Office' throttle admits where another timer is what caused this.
                    const ceilingReached = ssoRateErrorCeilingReached(gateAfter)
                    if (ceilingReached) {
                        console.debug(DateTime.now().toUTC().toISO(), 'AuthorizationProvider: Office SSO keeps throttling, telling the user')
                        setBootstrapBlocked({
                            retryAt: DateTime.now()
                                .toUTC()
                                .plus({ milliseconds: Math.max(penaltyMs, 0) }),
                        })
                    }

                    // A rate error is scheduled by the gate's own penalty; anything else keeps the
                    // existing exponential backoff. Retrying a throttle on the shorter ladder only
                    // spends calls the gate would suppress anyway.
                    let attempt: number
                    let retryInMs: number
                    if (penaltyMs > 0) {
                        attempt = bootstrapRetryAttemptRef.current + 1
                        bootstrapRetryAttemptRef.current = attempt
                        retryInMs = penaltyMs
                        scheduleBootstrapRetryIn(penaltyMs, `office rate limited (${outcome}), attempt ${attempt}`)
                    } else {
                        ;({ attempt, delayMs: retryInMs } = scheduleBootstrapRetry('office error'))
                    }

                    trackLoginFailure('bootstrap', error, {
                        mode: 'office-sso',
                        throttled,
                        terminal: ceilingReached,
                        attempt,
                        retryInMs,
                        rateErrorStreak: gateAfter.rateErrorStreak,
                        // Distinguishes "we asked too fast just now" from "this page load walked
                        // into a throttle it inherited" — the split the analysis could not make.
                        firstCallOfPageLoad,
                        msSinceLastCall,
                    })
                })
                .finally(() => {
                    bootstrapTokenRequestRef.current = undefined
                    console.debug(DateTime.now().toUTC().toISO(), 'AuthorizationProvider: getBootstrapToken (office) finished')
                })
        } else if (isPCAReady && user && hasRefreshToken(process.env.BOOTSTRAP_CLIENT_ID, user.homeAccountId)) {
            console.debug(DateTime.now().toUTC().toISO(), 'AuthorizationProvider: getBootstrapToken (silent) started')
            request = pca
                .acquireTokenSilent({
                    authenticationScheme: 'Bearer',
                    account: user,
                    scopes: AUTHENTICATION_SCOPES,
                })
                .then(result => {
                    const status = parseBootstrapToken(result.accessToken, user, setUser, bootstrapTokenRef, setBootstrapTokenExpiresAt)
                    if (status === 'fresh') {
                        bootstrapRetryAttemptRef.current = 0
                        trackLoginPhase('bootstrap', { mode: 'msal-silent' })
                        return
                    }
                    // Practically unreachable: MSAL always mints a fresh token. Still schedule a
                    // backoff retry (which re-enters the MSAL path, never Office.auth) rather than
                    // leaving no timer armed.
                    const { attempt, delayMs } = scheduleBootstrapRetry(`msal token ${status}`)
                    trackLoginFailure(
                        'bootstrap',
                        { message: `MSAL returned ${status} bootstrap token`, code: `${status}-bootstrap-token` },
                        { mode: 'msal-silent', attempt, retryInMs: delayMs },
                    )
                })
                .catch(error => {
                    console.debug(
                        DateTime.now().toUTC().toISO(),
                        `AuthorizationProvider: getBootstrapToken (silent) due to error: ${JSON.stringify(error)}`,
                    )
                    trackLoginFailure('bootstrap', error, { mode: 'msal-silent', willRedirect: true })
                    flushLoginTelemetry()
                    // Clear the timeout to prevent a racing retry while redirect is in progress
                    if (bootstrapTokenTimeoutRef.current) {
                        clearTimeout(bootstrapTokenTimeoutRef.current)
                        bootstrapTokenTimeoutRef.current = undefined
                    }
                    // Clear the ref so the redirect guard doesn't block, and clear the timeout to prevent a racing retry
                    bootstrapTokenRequestRef.current = undefined
                    return acquireBootstrapTokenRedirect()
                })
                .finally(() => {
                    bootstrapTokenRequestRef.current = undefined
                    console.debug(DateTime.now().toUTC().toISO(), 'AuthorizationProvider: getBootstrapToken (silent) finished')
                })
        } else {
            // Either there is no account at all, or MSAL has an account but no refresh token to
            // renew with. In that second case `acquireTokenSilent` would still try, fall into the
            // hidden iframe and burn `iframeHashTimeout` before ending up here anyway — so skip
            // straight to the redirect that was always going to happen. See HAM-4319.
            console.debug(
                DateTime.now().toUTC().toISO(),
                'AuthorizationProvider: skipping silent, no usable MSAL cache. account:',
                !!user,
                ', pca ready:',
                isPCAReady,
            )
            request = acquireBootstrapTokenRedirect()
        }

        bootstrapTokenRequestRef.current = request
        return request
    }

    /**
     * Invoke an HTTP redirect to get a user and bootstrap token.
     */
    const acquireBootstrapTokenRedirect = (): Promise<void> => {
        if (bootstrapTokenRequestRef.current) {
            return bootstrapTokenRequestRef.current
        }

        // The account decides both halves of this request, so resolve it once. MSAL turns an
        // account into a `login_hint` and then AAD signs that account straight back in; without
        // one it shows the account picker no matter how many accounts the browser has a session
        // for. Deriving the prompt from `getAllAccounts().length` while the hint came from `user`
        // let the two disagree: with an unhydrated `user` and accounts in the cache we sent
        // neither, and the picker appeared anyway — the one outcome this was meant to avoid.
        // `getActiveAccount()` covers the redirect that just returned, the single account covers
        // the ordinary case; anything else (no cache at all, or several accounts with none
        // active) genuinely has nothing to hint with, and there picking *is* the honest ask.
        const accounts = pca.getAllAccounts() ?? []
        const account = user ?? pca.getActiveAccount() ?? (accounts.length === 1 ? accounts[0] : undefined)

        console.debug(
            DateTime.now().toUTC().toISO(),
            'AuthorizationProvider: getBootstrapToken (redirect) started. account:',
            !!account,
            ', accounts in cache:',
            accounts.length,
        )

        const request: Promise<void> = pca
            .acquireTokenRedirect({
                authenticationScheme: 'Bearer',
                account,
                scopes: AUTHENTICATION_SCOPES,
                // AAD rejects prompt=select_account combined with a hint, and MSAL drops the hint
                // rather than the prompt — so these two are mutually exclusive by construction.
                prompt: account ? undefined : 'select_account',
            })
            .catch(error => {
                console.debug(
                    DateTime.now().toUTC().toISO(),
                    `AuthorizationProvider: getBootstrapToken (redirect) due to error: ${JSON.stringify(error)}`,
                )
                trackLoginFailure('bootstrap', error, { mode: 'msal-redirect' })
                throw error
            })
            .finally(() => {
                bootstrapTokenRequestRef.current = undefined
                console.debug(DateTime.now().toUTC().toISO(), 'AuthorizationProvider: getBootstrapToken (redirect) finished')
            })

        bootstrapTokenRequestRef.current = request
        return request
    }

    /**
     * Guards `handleRouterUnauthorized` against a refresh loop: holds the `uti` of the last
     * bootstrap token the router rejected with a 401. If MSAL/Office hands back that same token
     * again (e.g. the client clock runs far ahead, so the stale token passes the local freshness
     * check), we refresh at most once per token instead of hammering the router.
     */
    const last401RejectedUtiRef = useRef<string>()

    /**
     * The router answered 401 (HAM-1007/HAM-1024): it rejected the bootstrap assertion as (nearly)
     * expired. Drop the bootstrap token so `bootstrapTokenValid` flips to false, the bootstrap
     * effect silently fetches a fresh one, and the tokens effect then retries — reusing the
     * existing self-healing machinery instead of an ad-hoc retry.
     *
     * @returns true when a refresh was initiated, false when this token was already rejected once.
     */
    const handleRouterUnauthorized = (source: string): boolean => {
        let uti = undefined
        try {
            uti = bootstrapTokenRef.current && jwtDecode<OfficeTokenPayload>(bootstrapTokenRef.current).uti
        } catch (e) {
            console.error(e)
        }
        if (uti && uti === last401RejectedUtiRef.current) {
            console.debug(
                DateTime.now().toUTC().toISO(),
                `AuthorizationProvider: ${source} got 401 for bootstrap token`,
                uti,
                'again; not refreshing a second time',
            )
            return false
        }
        console.debug(
            DateTime.now().toUTC().toISO(),
            `AuthorizationProvider: ${source} got 401; dropping bootstrap token`,
            uti,
            'to force a silent refresh',
        )
        last401RejectedUtiRef.current = uti
        bootstrapTokenRef.current = undefined
        // Intentionally immediate (3s timer floor), not backed off: the rejected token was fresh
        // by the local clock, so bootstrapRetryAttemptRef is 0 here and only subsequent
        // stale/failed acquisitions slow down.
        setBootstrapTokenExpiresAt(DateTime.now().toUTC())
        return true
    }

    /**
     * Fetch available hosts when bootstrapToken is available, but we have not yet set a host.
     *
     */
    const acquireHosts = (): Promise<void> => {
        if (hostsRequestRef.current) {
            return hostsRequestRef.current
        }
        if (hostsUnavailableRef.current) {
            return Promise.resolve()
        }

        let decodedBootstrapToken = undefined
        try {
            decodedBootstrapToken = jwtDecode<OfficeTokenPayload>(bootstrapTokenRef.current)
        } catch (e) {
            console.error(e)
        }
        console.debug(
            DateTime.now().toUTC().toISO(),
            'AuthorizationProvider: acquireHosts started',
            host?.RowKey,
            '. bootstrapToken:',
            decodedBootstrapToken?.uti,
        )

        const request: Promise<void> = axios
            .get<Host[]>(`/api/host`, {
                baseURL: process.env.ROUTER_HOST,
                headers: { Authorization: `Bearer ${bootstrapTokenRef.current}`, SuppressErrors: 'true' },
            })
            .then(response => {
                setHosts(response.data)
                trackLoginPhase('hosts', { hostCount: response.data?.length ?? 0, autoSelected: response.data?.length === 1 })
                if (response.data?.length === 1) {
                    setHost(response.data[0])
                    // Skip the auto-tokens commit when an explicit acquireTokens is already running for this host:
                    // the in-flight fetch will deliver a fresh value. Committing stale auto-tokens here scheduled a
                    // near-now 3s expiry timeout that could double-fire acquireTokens on slow networks (see
                    // tmp/900-standalone-first-login-of-day.log).
                    if (response.data[0].tokens && !tokensRequestRef.current) {
                        setTokens(response.data[0].tokens)
                        setTokensHostKey(response.data[0].RowKey)
                        setTokensRevision(r => r + 1)
                        markTokensFetched()

                        // Take the earliest expiry across the tokens we actually received. `SecurityAuthority`
                        // is a string enum, so iterate its *values* (`'hammock'`, …) — the keys `tokens` is
                        // keyed by. Iterating with `for…in` yields the enum *names* (`'Hammock'`, …), which miss
                        // every lookup and drag the expiry down to `now` via getTokenExpiryDateTime's fallback.
                        // Skip absent tokens for the same reason.
                        let expireDateTime: DateTime | undefined = undefined
                        for (const authority of Object.values(SecurityAuthority)) {
                            const token = response.data[0].tokens?.[authority]
                            if (!token) {
                                continue
                            }
                            const dateTime = getTokenExpiryDateTime(token)
                            if (!expireDateTime || expireDateTime > dateTime) {
                                expireDateTime = dateTime
                            }
                        }
                        if (expireDateTime) {
                            console.debug(
                                DateTime.now().toUTC().toISO(),
                                'AuthorizationProvider: setting tokensExpiresAt to ',
                                expireDateTime.toISO(),
                            )
                            setTokensExpiresAt(expireDateTime.plus({ seconds: 3 }))
                        }
                    }
                } else if (!response.data?.some(h => h.RowKey === host?.RowKey)) {
                    // The selected host is not in the list this user can access. Also clear `host`
                    // itself, otherwise the tokens-acquisition effect keeps hammering /api/tokens on
                    // a host that will keep answering 404.
                    setHost(undefined)
                    setTokens(undefined)
                    setTokensExpiresAt(undefined)
                    setTokensHostKey(undefined)
                }
            })
            .catch(error => {
                const status = error.response?.status
                // A 4xx other than 401 is the router's final answer about this user, not a hiccup:
                // 404 means "no environments for you", anything else means we cannot determine them
                // at all. Both are terminal — keep asking and we just collect the same answer, which
                // is what left these users staring at a spinner (see login funnel, Aug 2026).
                const terminal = status === 404 || (status >= 400 && status < 500 && status !== 401)
                if (status === 404) {
                    setHosts([])
                } else if (status === 401) {
                    // HAM-1007/HAM-1024: the router rejected the bootstrap assertion as (nearly)
                    // expired; refresh it silently and let the hosts effect retry.
                    handleRouterUnauthorized('acquireHosts')
                }
                if (terminal) {
                    markHostsUnavailable(true)
                }
                console.error('AuthorizationProvider: error', error)
                trackLoginFailure('hosts', error, { terminal })
            })
            .finally(() => {
                hostsRequestRef.current = undefined
                console.debug(DateTime.now().toUTC().toISO(), 'AuthorizationProvider: acquireHosts finished')
            })

        hostsRequestRef.current = request
        return request
    }

    const acquireTokens = (): Promise<void | Tokens> => {
        if (tokensRequestRef.current) {
            return tokensRequestRef.current
        }

        // Snapshot the host we are fetching tokens for. If the user switches host while this request
        // is in flight, we discard the response rather than committing tokens that belong to the
        // previous host.
        const acquiringForHost = host
        if (!acquiringForHost) {
            return Promise.resolve()
        }

        let decodedBootstrapToken = undefined
        try {
            decodedBootstrapToken = jwtDecode<OfficeTokenPayload>(bootstrapTokenRef.current)
        } catch (e) {
            console.error(e)
        }
        console.debug(
            DateTime.now().toUTC().toISO(),
            'AuthorizationProvider: acquireTokens started',
            acquiringForHost.RowKey,
            '. bootstrapToken: ',
            decodedBootstrapToken?.uti,
        )

        const request: Promise<void | Tokens> = axios
            .get<Tokens>(`/api/tokens/${acquiringForHost.RowKey}`, {
                baseURL: process.env.ROUTER_HOST,
                headers: { Authorization: `Bearer ${bootstrapTokenRef.current}`, SuppressErrors: 'true' },
            })
            .then(response => {
                let decodedToken = undefined
                try {
                    decodedToken = jwtDecode<OfficeTokenPayload>(response.data?.[SecurityAuthority.Hammock]?.access_token)
                } catch (e) {
                    console.error(e)
                }
                console.debug(
                    DateTime.now().toUTC().toISO(),
                    'AuthorizationProvider: acquireTokens. New HAMMOCK token:',
                    decodedToken?.uti,
                    '. Expires at ',
                    getTokenExpiryDateTime(response.data?.[SecurityAuthority.Hammock]).toISO(),
                )
                setTokens(response.data)
                setTokensHostKey(acquiringForHost.RowKey)
                setTokensRevision(r => r + 1)
                markTokensFetched()
                setTokensExpiresAt(getTokenExpiryDateTime(response.data?.[SecurityAuthority.WebPubSub]))
                trackLoginPhase('tokens', { host: acquiringForHost.RowKey })
                return response.data
            })
            .catch(error => {
                console.error('AuthorizationProvider: error', error)
                trackLoginFailure('tokens', error, { host: acquiringForHost.RowKey, transientNetwork: isTransientNetworkError(error) })
                if (error.response?.status === 404) {
                    // The host does not exist (anymore) for this user — retrying is pointless. This
                    // happens when a stale host survives an account switch or is revoked server-side.
                    // Clear the host so the hosts-acquisition effect refetches the list this user can
                    // actually access, which also overwrites the stale persisted value.
                    console.debug(
                        DateTime.now().toUTC().toISO(),
                        'AuthorizationProvider: acquireTokens got 404; clearing host',
                        acquiringForHost.RowKey,
                    )
                    setHost(undefined)
                    setHosts(undefined)
                    setTokens(undefined)
                    setTokensExpiresAt(undefined)
                    setTokensHostKey(undefined)
                } else if (error.response?.status === 401) {
                    // HAM-1007/HAM-1024: the router rejected the bootstrap assertion as (nearly)
                    // expired; refresh it silently and let the tokens effect retry.
                    handleRouterUnauthorized('acquireTokens')
                } else if (isTransientNetworkError(error)) {
                    setTokensExpiresAt(DateTime.now().plus({ seconds: 5 }).toUTC())
                }
            })
            .finally(() => {
                tokensRequestRef.current = undefined
                console.debug(DateTime.now().toUTC().toISO(), 'AuthorizationProvider: acquireTokens finished')
            })

        tokensRequestRef.current = request
        return request
    }

    /**
     * ------------------
     */

    /**
     * Update user, mailbox, bootstrap token, hosts and host with information from the cookies, if present.
     */
    useEffect(() => {
        if (!isPCAReady) {
            return
        }
        if (!loadedFirstVisit || firstVisit) {
            console.debug(DateTime.now().toUTC().toISO(), 'AuthorizationProvider: not hydrating user because this is the first visit.')
            return
        }

        if (authenticationStorageLoaded) {
            console.debug(DateTime.now().toUTC().toISO(), 'AuthorizationProvider: hydrating authenticationStorage')
            setUser(user ?? authenticationStorage?.user)
            setMailbox(mailbox ?? authenticationStorage?.mailbox)
        }
        setIsUserHydrated(authenticationStorageLoaded)
    }, [isPCAReady, authenticationStorageLoaded, firstVisit])

    useEffect(() => {
        if (!isUserHydrated) {
            return
        }
        setAuthenticationStorage({ identified, authenticated, user, mailbox })
    }, [isUserHydrated, authenticationStorageLoaded, user, mailbox])

    useEffect(() => {
        if (!isUserHydrated) {
            return
        }
        // Without a user there's no user-scoped host storage to hydrate, but downstream effects
        // (notably the bootstrap-acquisition effect that triggers the MSAL redirect in standalone
        // mode) still need to run, so mark hydration complete immediately. Never flip
        // `isBootstrapHydrated` back to false once true — that would gate-block the post-login
        // window where `user` is set but `authorizationHostStorageLoaded` hasn't caught up yet.
        if (!user) {
            setIsBootstrapHydrated(true)
            return
        }
        if (!storageMatchesUser) {
            // Account switch in progress: the loaded storage belongs to the previous account. Skip
            // hydration entirely — `acquireHosts` will fetch the new account's hosts once `host` is
            // still unset. Deliberately not re-run when `authenticationStorage` catches up: by then
            // the storage values may still be the previous account's (the per-key reload lands a
            // render later), so re-hydrating here would reopen the race.
            console.debug(
                DateTime.now().toUTC().toISO(),
                'AuthorizationProvider: skipping authorizationHostStorage hydration; storage belongs to a different account',
            )
            setIsBootstrapHydrated(true)
            return
        }
        if (authorizationHostStorageLoaded) {
            console.debug(DateTime.now().toUTC().toISO(), 'AuthorizationProvider: hydrating authorizationHostStorage')
            const storedBootstrapToken = bootstrapTokenRef.current ?? authorizationHostStorage?.bootstrapToken
            if (isBootstrapTokenFresh(storedBootstrapToken)) {
                parseBootstrapToken(storedBootstrapToken, user, setUser, bootstrapTokenRef, setBootstrapTokenExpiresAt)
                trackLoginPhase('bootstrap', { mode: 'storage' })
            } else if (storedBootstrapToken) {
                console.debug(DateTime.now().toUTC().toISO(), 'AuthorizationProvider: discarding stale stored bootstrapToken')
            }
            setHosts(hosts ?? authorizationHostStorage?.hosts)
            setHost(host ?? authorizationHostStorage?.host)
            setIsBootstrapHydrated(true)
        }
    }, [isUserHydrated, authorizationHostStorageLoaded, user])

    useEffect(() => {
        // Skip when `storagePrefix` is undefined (e.g. just after `resetUser` clears the user).
        // `useLocalStorage` falls back to `hostPrefix='global'` for an undefined `hostKey`, so writing
        // here would persist to `global-AuthorizationHostStorage` instead of a per-user key.
        // Also skip while `storagePrefix` still belongs to the previous account during an account
        // switch: writing then would persist the new account's bootstrapToken under the previous
        // account's key.
        if (!isBootstrapHydrated || storagePrefix === undefined || !storageMatchesUser) {
            return
        }
        setAuthorizationHostStorage({ bootstrapToken: bootstrapTokenRef.current, hosts, host })
    }, [isBootstrapHydrated, authorizationHostStorageLoaded, bootstrapTokenExpiresAt, hosts, host, storagePrefix])

    /**
     * Once the PCA is ready and the cookies are hydrated, hydrate the localstorage.
     */
    useEffect(() => {
        if (!isBootstrapHydrated) {
            return
        }
        if (authorizationTokensStorageLoaded) {
            console.debug(DateTime.now().toUTC().toISO(), 'AuthorizationProvider: hydrating authorizationTokensStorage')
            const hydratedTokens = tokens ?? authorizationTokensStorage?.tokens
            if (hydratedTokens && isTokensNotExpired(hydratedTokens)) {
                setTokens(hydratedTokens)
                setTokensExpiresAt(getTokenExpiryDateTime(hydratedTokens?.[SecurityAuthority.WebPubSub]))
                // The tokens localStorage key is scoped by host.RowKey via tokensStoragePrefix, so any
                // hydrated tokens are by construction for the currently selected host.
                if (host?.RowKey) {
                    setTokensHostKey(host.RowKey)
                }
            } else if (hydratedTokens) {
                console.debug(DateTime.now().toUTC().toISO(), 'AuthorizationProvider: discarding stale stored tokens')
            }
        }
        setIsHydrated(authorizationTokensStorageLoaded)
    }, [isBootstrapHydrated, authorizationTokensStorageLoaded])

    useEffect(() => {
        // Same guard as the host-storage persist effect: when `tokensStoragePrefix` is undefined
        // (no host selected, e.g. just after logout) the underlying `useLocalStorage` would write to
        // `global-AuthorizationTokensStorage`. Skip until we have a real per-(host, user) prefix.
        if (!isHydrated || tokensStoragePrefix === undefined) {
            return
        }
        setAuthorizationTokensStorage({ tokens, roles })
    }, [isHydrated, authorizationTokensStorageLoaded, tokens, tokensStoragePrefix])

    /**
     * ------------------
     * Initialize all necessary fields after the localstorage and cookies have been hydrated.
     */

    /**
     * Check if the user is currently in a shared mailbox.
     */
    useEffect(() => {
        if (isOfficeInitialized()) {
            checkSharedMailboxEmail(setMailbox)
        } else {
            setMailbox(user?.username)
        }
    }, [authenticationStorageLoaded, user])

    /**
     * Acquire a bootstraptoken.
     */
    useEffect(() => {
        if (isBootstrapHydrated && (isOfficeInitialized() || user) && !bootstrapTokenValid) {
            acquireBootstrapTokenSilent()
        } else if (isBootstrapHydrated && !isOfficeInitialized() && !user) {
            acquireBootstrapTokenRedirect()
        }
    }, [isBootstrapHydrated, user, bootstrapTokenValid, loadedFirstVisit, firstVisit])

    /**
     * Once we have a bootstrap token but no host has been selected, fetch available hosts.
     */
    useEffect(() => {
        if (isBootstrapHydrated && bootstrapTokenValid && !host) {
            acquireHosts()
        }
    }, [isBootstrapHydrated, bootstrapTokenValid, host])

    /**
     * Set the expiry timeout for the bootstrap token. Use a minimum of 3 seconds and a maximum of 30 minutes.
     */
    useEffect(() => {
        if (isBootstrapHydrated && bootstrapTokenExpiresAt) {
            console.debug(
                DateTime.now().toUTC().toISO(),
                'AuthorizationProvider: bootstrapTokenExpiresAt changed to',
                bootstrapTokenExpiresAt.toJSON(),
            )

            const timer = Math.min(Math.max(bootstrapTokenExpiresAt.toMillis() - DateTime.now().toUTC().toMillis(), 3_000), 1_800_000)
            console.debug(DateTime.now().toUTC().toISO(), 'AuthorizationProvider: Setting timeout for bootstrapToken in', timer, 'milliseconds.')

            if (bootstrapTokenTimeoutRef.current) {
                clearTimeout(bootstrapTokenTimeoutRef.current)
                bootstrapTokenTimeoutRef.current = undefined
            }
            bootstrapTokenTimeoutRef.current = setTimeout(() => acquireBootstrapTokenSilent(), timer)
            return () => {
                console.debug(DateTime.now().toUTC().toISO(), 'AuthorizationProvider: clearing timeout for bootstrapToken')
                if (bootstrapTokenTimeoutRef.current) {
                    clearTimeout(bootstrapTokenTimeoutRef.current)
                    bootstrapTokenTimeoutRef.current = undefined
                }
            }
        } else if (bootstrapTokenTimeoutRef.current) {
            console.debug(
                DateTime.now().toUTC().toISO(),
                'AuthorizationProvider: bootstrapTokenExpiresAt changed to undefined, clearing timeout for bootstrapToken',
            )
            clearTimeout(bootstrapTokenTimeoutRef.current)
            bootstrapTokenTimeoutRef.current = undefined
        }
        return () => {}
    }, [isBootstrapHydrated, bootstrapTokenExpiresAt])

    /**
     * Fetch the tokens when we have a bootstrap token but no valid pub sub token.
     *
     * `tokens` is in the deps because the host-change effect below clears tokens to `undefined`
     * when the user switches host. Without `tokens` here, that clear would not retrigger this
     * effect (host didn't change in that follow-up render) and the user would be stuck without
     * fresh tokens for the new host.
     */
    useEffect(() => {
        if (isHydrated && bootstrapTokenValid && host && !isTokensNotExpired(tokens)) {
            acquireTokens()
        } else if (isHydrated && !bootstrapTokenValid) {
            console.debug(DateTime.now().toUTC().toISO(), 'AuthorizationProvider: bootstrapToken is undefined')
        } else if (isHydrated && !host) {
            console.debug(DateTime.now().toUTC().toISO(), 'AuthorizationProvider: host is undefined')
        }
    }, [isHydrated, bootstrapTokenValid, host, tokens])

    /**
     * When the host changes (including to undefined on logout), drop any tokens that were loaded
     * for a different host. This guarantees that downstream consumers observing `environmentKey`
     * see it flip to `undefined` until tokens for the new host arrive, instead of briefly seeing
     * the new host paired with the previous host's tokens.
     */
    useEffect(() => {
        if (tokensHostKey && tokensHostKey !== host?.RowKey) {
            console.debug(DateTime.now().toUTC().toISO(), 'AuthorizationProvider: host changed; dropping tokens that belonged to', tokensHostKey)
            setTokens(undefined)
            setTokensExpiresAt(undefined)
            setTokensHostKey(undefined)
        }
    }, [host?.RowKey])

    /**
     * Set the expiry timeout for the tokens. Use a minimum of 3 seconds and a maximum of 25 minutes.
     * Also gated on `bootstrapTokenValid`: without a fresh bootstrap, the timer's `acquireTokens`
     * call would hit /api/tokens with a stale Authorization header and produce a guaranteed 401
     * (HAM-1007).
     */
    useEffect(() => {
        if (isHydrated && tokensExpiresAt && bootstrapTokenValid) {
            console.debug(DateTime.now().toUTC().toISO(), 'AuthorizationProvider: tokensExpiresAt changed to', tokensExpiresAt.toJSON())

            const timer = Math.min(Math.max(tokensExpiresAt.toMillis() - DateTime.now().toUTC().toMillis(), 3_000), 1_500_000)
            console.debug(DateTime.now().toUTC().toISO(), 'AuthorizationProvider: Setting timeout for tokens in', timer, 'milliseconds.')

            if (tokensTimeoutRef.current) {
                clearTimeout(tokensTimeoutRef.current)
                tokensTimeoutRef.current = undefined
            }
            tokensTimeoutRef.current = setTimeout(() => (host ? acquireTokens() : Promise.resolve()), timer)
            return () => {
                console.debug(DateTime.now().toUTC().toISO(), 'AuthorizationProvider: clearing timeout for tokens')
                if (tokensTimeoutRef.current) {
                    clearTimeout(tokensTimeoutRef.current)
                    tokensTimeoutRef.current = undefined
                }
            }
        } else if (tokensTimeoutRef.current) {
            console.debug(DateTime.now().toUTC().toISO(), 'AuthorizationProvider: tokensExpiresAt changed to undefined, clearing timeout for tokens')
            clearTimeout(tokensTimeoutRef.current)
            tokensTimeoutRef.current = undefined
        }
        return () => {}
    }, [isHydrated, host, tokensExpiresAt, bootstrapTokenValid])

    /**
     * Mark the initial login as complete. After this, silent refresh cycles no longer emit
     * success telemetry and their failures are tagged with trigger 'refresh' (see loginTelemetry.ts).
     */
    useEffect(() => {
        if (authenticated && !initialLoginTrackedRef.current) {
            initialLoginTrackedRef.current = true
            trackLoginPhase('authenticated', { host: host?.RowKey })
            markInitialLoginComplete()
        }
    }, [authenticated])

    useEffect(() => {
        const handleOffline = () => {
            console.debug(DateTime.now().toUTC().toISO(), 'AuthorizationProvider: window/tab went offline')
            clearTimeout(tokensTimeoutRef.current)
            clearTimeout(bootstrapTokenTimeoutRef.current)
        }

        const handleOnline = () => {
            console.debug(DateTime.now().toUTC().toISO(), 'AuthorizationProvider: window/tab went online')
            // Re-arm with a NEW DateTime reference: the timer effects' deps compare by reference,
            // and React bails out entirely on a reference-equal state update, so re-setting the
            // same instance would leave the timers cleared after an offline period.
            setTokensExpiresAt(current => current?.plus({ milliseconds: 0 }))
            setBootstrapTokenExpiresAt(current => current?.plus({ milliseconds: 0 }))
        }

        window.addEventListener('offline', handleOffline)
        window.addEventListener('online', handleOnline)
        window.addEventListener('visibilitychange', handleOnline)

        return () => {
            window.removeEventListener('offline', handleOffline)
            window.removeEventListener('online', handleOnline)
            window.removeEventListener('visibilitychange', handleOnline)
        }
    }, [])

    const updateTokens = (authority: SecurityAuthority): Promise<ServerAuthorizationTokenResponse | WebPubSubToken> => {
        let request = Promise.resolve()

        if (!isBootstrapTokenFresh(bootstrapTokenRef.current)) {
            request = request.then(() => acquireBootstrapTokenSilent())
        }
        if (!hosts || !host) {
            // Re-check freshness: acquireBootstrapTokenSilent swallows failures (the Office flow
            // resolves after an error), so the ref may still hold no usable token here.
            request = request.then(() => {
                if (!isBootstrapTokenFresh(bootstrapTokenRef.current)) {
                    console.debug(
                        DateTime.now().toUTC().toISO(),
                        'AuthorizationProvider: skipping acquireHosts; bootstrap acquisition did not yield a fresh token',
                    )
                    return Promise.resolve()
                }
                return acquireHosts()
            })
        }

        return request
            .then(() => (host && isBootstrapTokenFresh(bootstrapTokenRef.current) ? acquireTokens() : Promise.resolve()))
            .then(tokens => tokens?.[authority])
    }

    const getBaseUrl = (destination: Destination = Destination.Hammock): string | undefined => {
        if (!host) {
            return undefined
        }

        switch (destination) {
            case Destination.SharePoint:
            case Destination.Exchange:
                return graphBaseUrl
            case Destination.Documents:
                return `${graphBaseUrl}/drives/${host?.DriveId}`
            case Destination.ReplicationEmail:
                return `${graphBaseUrl}/users/${host?.ReplicationMail}`
            case Destination.Email:
                return `${graphBaseUrl}/${mailbox ? 'users/' + mailbox : 'me'}`
            case Destination.PrivateEmail:
                return `${graphBaseUrl}/me`
            case Destination.Hammock:
            default:
                return host?.Backend
        }
    }

    const resetUser = () => {
        //Reset both to force both Standalone and Outlook to refresh user and bootstrap token.
        setUser(undefined)
        bootstrapTokenRef.current = undefined
        setBootstrapTokenExpiresAt(undefined)
        setAuthenticationStorage(undefined)
        setHost(undefined)
        // Also clear `hosts`: in Outlook mode `storagePrefix` stays defined, so the persist effect
        // can fire once more before the reload — with both cleared it writes an empty object instead
        // of re-persisting the stale hosts list.
        setHosts(undefined)
        // A reset must force `acquireHosts` on the next boot, so a server-side changed DriveId
        // (SharePoint site move) is picked up instead of rehydrating the stale host. Remove the
        // persisted host entries directly (not via setAuthorizationHostStorage) so the removal is
        // synchronous before the reload, and sweep by substring so historical key-format variants
        // (pathname part, homeAccountId vs Outlook e-mail) are cleared as well.
        clearLocalStorageKeys('AuthorizationHostStorage', key => key.includes('AuthorizationHostStorage'))
        // Also drop the persisted tokens: they are tenant-bound, so after e.g. a SharePoint tenant
        // move the cached (still unexpired) tokens keep failing against the new tenant. The tokens
        // hydration only checks expiry, not which tenant a token was issued for, so a reset must
        // force a fresh `acquireTokens` as well.
        clearLocalStorageKeys('AuthorizationTokensStorage', key => key.includes('AuthorizationTokensStorage'))
        clearLocalStorageGlobals()
        // The user deliberately restarted (Support) or switched accounts (SelectHost), so the
        // current login journey is over: the reload below must start a new one instead of
        // resuming this one across the navigation.
        clearLoginJourney()
        pca.clearCache({ account: user })
            .then(() => pca.setActiveAccount(null))
            .then(() => window.location.reload())
    }

    return (
        <AuthorizationContext.Provider
            value={{
                identified,
                authenticated,
                user,
                mailbox,
                bootstrapToken: bootstrapTokenRef.current,
                hosts,
                hostsUnavailable,
                host,
                tokens,
                roles,
                environmentKey,
                tokensRevision,
                updateTokens,
                setHost: (newHost: Host) => {
                    if (newHost !== undefined) {
                        if (host && newHost.RowKey !== host.RowKey) {
                            // Mid-session environment switch: the hash still mirrors the previous
                            // host's page. Reset it so the remounted Router restores the new
                            // host's own last visited page instead.
                            window.location.hash = '/'
                        }
                        setHost(newHost)
                    }
                },
                getBaseUrl,
                reset: resetUser,
                bootstrapBlocked,
                retryBootstrap,
            }}
        >
            {props.children}
        </AuthorizationContext.Provider>
    )
}

// noinspection JSUnusedGlobalSymbols
export default AuthorizationContext
